import { AlertCircle } from 'lucide-react';
import { MoveInSlotInfo } from '../../services/tenantApi';
import { MoveInConfirmedCard, MoveInPendingConfirmCard } from './MoveInAppointmentCards';
import { MoveInWeekCalendar } from './MoveInWeekCalendar';

export type MoveInAppointmentMode = 'confirmed' | 'picker' | 'pending-confirm';

export interface MoveInAppointmentBookingFlowProps {
  mode: MoveInAppointmentMode;
  bookingError: string | null;
  scheduledDate: string | null;
  appointmentStartTime: string | null;
  appointmentEndTime: string | null;
  isByTheBed?: boolean;
  lease?: { propertyName?: string | null; unitNumber?: string | null; propertyUnitCount?: number; address?: string | null } | null;
  confirming: boolean;
  booking: boolean;
  calendarLoading: boolean;
  dateWindow: string[];
  slotsByDate: Record<string, MoveInSlotInfo[]>;
  rescheduleFromStart: string | null;
  canGoBack: boolean;
  canGoForward: boolean;
  blockedDates?: Set<string>;
  todayET: string;
  rescheduleAvailable?: boolean;
  onReschedule: () => void;
  onConfirm: () => void;
  onBookDifferent: () => void;
  onBook: (slot: MoveInSlotInfo) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
}

export function MoveInAppointmentBookingFlow({
  mode,
  bookingError,
  scheduledDate,
  appointmentStartTime,
  appointmentEndTime,
  lease,
  confirming,
  booking,
  calendarLoading,
  dateWindow,
  slotsByDate,
  rescheduleFromStart,
  canGoBack,
  canGoForward,
  blockedDates,
  todayET,
  rescheduleAvailable = true,
  onReschedule,
  onConfirm,
  onBookDifferent,
  onBook,
  onPrevWeek,
  onNextWeek,
}: MoveInAppointmentBookingFlowProps) {
  return (
    <>
      {bookingError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900/40 rounded-lg">
          <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{bookingError}</p>
        </div>
      )}

      {mode === 'confirmed' && appointmentStartTime && appointmentEndTime && (
        <MoveInConfirmedCard
          startTime={appointmentStartTime}
          endTime={appointmentEndTime}
          onReschedule={onReschedule}
          lease={lease}
          rescheduleAvailable={rescheduleAvailable}
        />
      )}

      {mode === 'pending-confirm' && scheduledDate && (
        <MoveInPendingConfirmCard
          scheduledDate={scheduledDate}
          onConfirm={onConfirm}
          onBookDifferent={onBookDifferent}
          confirming={confirming}
        />
      )}

      {mode === 'picker' && (
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            {rescheduleFromStart
              ? 'Select a new time slot for your move-in appointment. You may keep your current time or pick a later slot.'
              : 'Select a one-hour time slot for your move-in appointment. All times are in Eastern Time.'}
          </p>
          <MoveInWeekCalendar
            dates={dateWindow}
            slotsByDate={slotsByDate}
            loading={calendarLoading}
            todayET={todayET}
            booking={booking}
            onBook={onBook}
            onPrevWeek={onPrevWeek}
            onNextWeek={onNextWeek}
            canGoBack={canGoBack}
            canGoForward={canGoForward}
            scheduledDateStart={rescheduleFromStart}
            actualAppointmentStart={rescheduleFromStart ? appointmentStartTime : null}
            blockedDates={blockedDates}
          />
        </div>
      )}
    </>
  );
}
